import { getProjectSaveActions } from "../stores/projectSaveStore";

export const FLOOR_APPEARANCE_EVENT = "floor-appearance-options-changed";

export const FLOOR_APPEARANCE_MODES = {
  light: "light",
  dark: "dark",
};

export const FLOOR_FINISHES = {
  matte: "matte",
  satin: "satin",
  glossy: "glossy",
};

const FLOOR_FINISH_BY_MODE = {
  [FLOOR_APPEARANCE_MODES.light]: FLOOR_FINISHES.glossy,
  [FLOOR_APPEARANCE_MODES.dark]: FLOOR_FINISHES.matte,
};

const FLOOR_FINISH_MATERIALS = {
  [FLOOR_FINISHES.matte]: {
    metalness: 0.0,
    roughness: 0.82,
    reflectivity: 0.04,
    clearcoat: 0.04,
    clearcoatRoughness: 0.88,
  },
  [FLOOR_FINISHES.satin]: {
    metalness: 0.02,
    roughness: 0.46,
    reflectivity: 0.16,
    clearcoat: 0.34,
    clearcoatRoughness: 0.42,
  },
  [FLOOR_FINISHES.glossy]: {
    metalness: 0.1,
    roughness: 0.2,
    reflectivity: 0.3,
    clearcoat: 0.8,
    clearcoatRoughness: 0.1,
  },
};

export const FLOOR_APPEARANCE_PALETTES = {
  light: {
    floorColor: 0xffffff,
    gridCenterColor: 0x888888,
    gridLineColor: 0xaaaaaa,
    lineColor: 0xaaaaaa,
    metalness: 0.1,
    roughness: 0.2,
    reflectivity: 0.3,
    clearcoat: 0.8,
    clearcoatRoughness: 0.1,
  },
  dark: {
    floorColor: 0x050505,
    gridCenterColor: 0xd1d5db,
    gridLineColor: 0xd1d5db,
    lineColor: 0xd1d5db,
    metalness: 0.0,
    roughness: 0.72,
    reflectivity: 0.05,
    clearcoat: 0.12,
    clearcoatRoughness: 0.85,
  },
};

const DEFAULT_FLOOR_APPEARANCE_OPTIONS = {
  mode: FLOOR_APPEARANCE_MODES.light,
};

let floorAppearanceOptions = { ...DEFAULT_FLOOR_APPEARANCE_OPTIONS };

function normalizeFloorColorHex(color) {
  if (typeof color !== "string") return undefined;
  const trimmed = color.trim();
  const match = /^#?([0-9a-f]{6})$/i.exec(trimmed);
  return match ? `#${match[1].toLowerCase()}` : undefined;
}

function normalizeFloorOpacity(opacity) {
  if (opacity == null) return undefined;
  const value = Number(opacity);
  return Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : undefined;
}

function normalizeFloorFinish(finish) {
  return Object.values(FLOOR_FINISHES).includes(finish) ? finish : undefined;
}

function colorHexToNumber(color) {
  const normalized = normalizeFloorColorHex(color);
  return normalized ? Number.parseInt(normalized.slice(1), 16) : undefined;
}

function numberToRelativeLuminance(color) {
  const channels = [16, 8, 0].map((shift) => {
    const value = ((Number(color || 0) >> shift) & 0xff) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

function getAdaptiveLightGridColors(floorColor, basePalette) {
  const luminance = numberToRelativeLuminance(floorColor);
  if (luminance >= 0.82) {
    return {
      gridCenterColor: basePalette.gridCenterColor,
      gridLineColor: basePalette.gridLineColor,
      lineColor: basePalette.lineColor,
    };
  }
  if (luminance >= 0.42) {
    return {
      gridCenterColor: 0x4b5563,
      gridLineColor: 0x6b7280,
      lineColor: 0x6b7280,
    };
  }
  return {
    gridCenterColor: 0xd1d5db,
    gridLineColor: 0xd1d5db,
    lineColor: 0xd1d5db,
  };
}

function numberToColorHex(color) {
  return `#${Number(color || 0)
    .toString(16)
    .padStart(6, "0")
    .slice(-6)}`;
}

function normalizeMode(mode) {
  return mode === FLOOR_APPEARANCE_MODES.dark
    ? FLOOR_APPEARANCE_MODES.dark
    : FLOOR_APPEARANCE_MODES.light;
}

export function getFloorAppearanceOptions() {
  return floorAppearanceOptions;
}

export function getFloorAppearancePalette(options = floorAppearanceOptions) {
  const mode = normalizeMode(options?.mode);
  const basePalette = FLOOR_APPEARANCE_PALETTES[mode] || FLOOR_APPEARANCE_PALETTES.light;
  const floorColor = colorHexToNumber(options?.floorColorHex) ?? basePalette.floorColor;
  const finish = normalizeFloorFinish(options?.floorFinish);
  const finishMaterial = finish ? FLOOR_FINISH_MATERIALS[finish] : {};
  const adaptiveGridColors =
    mode === FLOOR_APPEARANCE_MODES.light
      ? getAdaptiveLightGridColors(floorColor, basePalette)
      : {
          gridCenterColor: basePalette.gridCenterColor,
          gridLineColor: basePalette.gridLineColor,
          lineColor: basePalette.lineColor,
        };
  return {
    ...basePalette,
    ...adaptiveGridColors,
    ...finishMaterial,
    floorColor,
    floorFinish: finish || FLOOR_FINISH_BY_MODE[mode],
    floorOpacity: normalizeFloorOpacity(options?.floorOpacity) ?? 1,
  };
}

export function getFloorAppearancePresetColorHex(mode = floorAppearanceOptions.mode) {
  const palette = FLOOR_APPEARANCE_PALETTES[normalizeMode(mode)] || FLOOR_APPEARANCE_PALETTES.light;
  return numberToColorHex(palette.floorColor);
}

function applyFloorAppearanceOptions(patch = {}, { markDirty = false } = {}) {
  const nextMode = normalizeMode(patch.mode ?? floorAppearanceOptions.mode);
  const nextOptions = {
    ...floorAppearanceOptions,
    ...patch,
    mode: nextMode,
  };
  if ("floorColorHex" in patch) {
    const color = normalizeFloorColorHex(patch.floorColorHex);
    if (color) {
      nextOptions.floorColorHex = color;
    } else {
      delete nextOptions.floorColorHex;
    }
  }
  if ("floorOpacity" in patch) {
    const opacity = normalizeFloorOpacity(patch.floorOpacity);
    if (opacity === undefined) {
      delete nextOptions.floorOpacity;
    } else {
      nextOptions.floorOpacity = opacity;
    }
  }
  if ("floorFinish" in patch) {
    const finish = normalizeFloorFinish(patch.floorFinish);
    if (finish) {
      nextOptions.floorFinish = finish;
    } else {
      delete nextOptions.floorFinish;
    }
  }
  const changed =
    nextOptions.mode !== floorAppearanceOptions.mode ||
    nextOptions.floorColorHex !== floorAppearanceOptions.floorColorHex ||
    nextOptions.floorOpacity !== floorAppearanceOptions.floorOpacity ||
    nextOptions.floorFinish !== floorAppearanceOptions.floorFinish;
  if (!changed) return floorAppearanceOptions;
  floorAppearanceOptions = nextOptions;
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(FLOOR_APPEARANCE_EVENT, { detail: floorAppearanceOptions })
    );
  }
  if (markDirty) {
    getProjectSaveActions().markProjectDirty();
  }
  return floorAppearanceOptions;
}

export function setFloorAppearanceOptions(patch = {}) {
  return applyFloorAppearanceOptions(patch, { markDirty: true });
}

export function applyProjectFloorAppearanceOptions(patch = {}) {
  return applyFloorAppearanceOptions(patch, { markDirty: false });
}

export function subscribeFloorAppearanceOptions(listener) {
  if (typeof window === "undefined") return () => {};
  const handler = () => listener();
  window.addEventListener(FLOOR_APPEARANCE_EVENT, handler);
  return () => window.removeEventListener(FLOOR_APPEARANCE_EVENT, handler);
}
